import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { apiFetch } from '../lib/api.ts';
import { dateTime } from '../lib/format.ts';

interface SyncStateResp {
  state: Array<{
    entityType: string;
    lastSyncedAt: string | null;
    lastRunStatus: string | null;
    lastError: string | null;
    recordsSynced: number;
  }>;
  recentRuns: Array<{
    id: number;
    runId: string;
    mode: string;
    entityType: string;
    startedAt: string;
    completedAt: string | null;
    status: string;
    recordsSynced: number;
    recordsExpected: number | null;
    errorsCount: number;
  }>;
  recentEvents: Array<{
    id: number;
    eventTime: string;
    level: string;
    msg: string;
    context: unknown;
  }>;
}

export function AdminSyncStatePage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'sync-state'],
    queryFn: () => apiFetch<SyncStateResp>('/api/admin/sync-state'),
    refetchInterval: 30_000,
  });

  return (
    <>
      <header className="main-header">
        <h1 className="main-title">Sync state</h1>
        <span className="main-subtitle">refreshes every 30s</span>
      </header>
      <Link to="/admin" className="back-link">← Admin</Link>

      {isLoading && <div className="loading">Loading…</div>}
      {error && <div className="error">{(error as Error).message}</div>}

      {data && (
        <>
          <section className="panel">
            <header className="panel-header">
              <h2 className="panel-title">Per-entity watermarks</h2>
              <span className="panel-subtitle">{data.state.length} entries</span>
            </header>
            {data.state.length === 0 ? (
              <div className="empty">
                No sync_state rows yet — waiting for the agent's first
                completed run to populate per-entity watermarks.
              </div>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Entity</th>
                    <th>Last synced</th>
                    <th>Status</th>
                    <th className="num">Records</th>
                    <th>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {data.state.map((s) => (
                    <tr key={s.entityType}>
                      <td className="mono">{s.entityType}</td>
                      <td className="mono">
                        {s.lastSyncedAt ? dateTime(s.lastSyncedAt) : '—'}
                      </td>
                      <td>
                        <span className={`badge ${statusBadge(s.lastRunStatus)}`}>
                          {s.lastRunStatus ?? '—'}
                        </span>
                      </td>
                      <td className="num">{s.recordsSynced}</td>
                      <td>{s.lastError ?? ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <section className="panel">
            <header className="panel-header">
              <h2 className="panel-title">Recent runs</h2>
              <span className="panel-subtitle">{data.recentRuns.length} most recent</span>
            </header>
            {data.recentRuns.length === 0 ? (
              <div className="empty">
                No sync_runs rows yet — populated as the agent reports
                completed sync cycles.
              </div>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Run</th>
                    <th>Mode</th>
                    <th>Entity</th>
                    <th>Started</th>
                    <th>Completed</th>
                    <th>Status</th>
                    <th className="num">Synced</th>
                    <th className="num">Expected</th>
                    <th className="num">Errors</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recentRuns.map((r) => (
                    <tr key={r.id}>
                      <td className="mono" style={{ fontSize: 11 }}>{r.runId}</td>
                      <td className="mono">{r.mode}</td>
                      <td className="mono">{r.entityType}</td>
                      <td className="mono" style={{ fontSize: 11 }}>{dateTime(r.startedAt)}</td>
                      <td className="mono" style={{ fontSize: 11 }}>
                        {r.completedAt ? dateTime(r.completedAt) : '—'}
                      </td>
                      <td>
                        <span className={`badge ${statusBadge(r.status)}`}>{r.status}</span>
                      </td>
                      <td className="num">{r.recordsSynced}</td>
                      <td className="num">{r.recordsExpected ?? '—'}</td>
                      <td className="num">{r.errorsCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <section className="panel">
            <header className="panel-header">
              <h2 className="panel-title">Agent log feed</h2>
              <span className="panel-subtitle">{data.recentEvents.length} most recent</span>
            </header>
            {data.recentEvents.length === 0 ? (
              <div className="empty">
                No agent_log_events. Agent streams structured logs to{' '}
                <code>/api/agent/log-event</code> while running.
              </div>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Level</th>
                    <th>Message</th>
                    <th>Context</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recentEvents.map((e) => (
                    <tr key={e.id}>
                      <td className="mono" style={{ fontSize: 11 }}>
                        {dateTime(e.eventTime)}
                      </td>
                      <td>
                        <span className={`badge ${levelBadge(e.level)}`}>{e.level}</span>
                      </td>
                      <td>{e.msg}</td>
                      <td className="mono" style={{ fontSize: 11, color: 'var(--text-dim)' }}>
                        {e.context ? JSON.stringify(e.context) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}
    </>
  );
}

function statusBadge(s: string | null | undefined): string {
  if (!s) return 'badge-neutral';
  if (s === 'success') return 'badge-ok';
  if (s === 'error') return 'badge-cancel';
  if (s === 'partial') return 'badge-pending';
  return 'badge-neutral';
}

function levelBadge(s: string): string {
  if (s === 'error' || s === 'fatal') return 'badge-cancel';
  if (s === 'warn') return 'badge-pending';
  return 'badge-neutral';
}
